import { useEffect, useMemo, useRef, useState } from 'react';
import { type TranscriptWord } from '@captionflow/caption-engine';
import { useEditor, type Broll } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';
import { TRANSITION_LABEL } from '../../lib/transitions';

const FPS = 30;
const BASE_PXMS = 0.25; // 100% zoom

/* ---- crisp inline icons (currentColor) ---- */
const Play = () => (
  <svg width="13" height="14" viewBox="0 0 13 14" fill="currentColor" aria-hidden>
    <path d="M1 1.4c0-.8.9-1.3 1.6-.9l9 5.6c.6.4.6 1.4 0 1.8l-9 5.6c-.7.4-1.6-.1-1.6-.9V1.4Z" />
  </svg>
);
const Pause = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor" aria-hidden>
    <rect x="1" y="1" width="3.4" height="12" rx="1.2" />
    <rect x="7.6" y="1" width="3.4" height="12" rx="1.2" />
  </svg>
);
const VolIcon = ({ level }: { level: number }) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 9v6h3.5L13 19V5L7.5 9H4Z" fill="currentColor" stroke="none" />
    {level <= 0.001 ? (
      <path d="M17 9l4 4m0-4l-4 4" />
    ) : (
      <>
        <path d="M16.5 8.5a5 5 0 0 1 0 7" />
        {level > 0.55 && <path d="M19 6a8 8 0 0 1 0 12" />}
      </>
    )}
  </svg>
);
const Minus = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M3 7h8" />
  </svg>
);
const Plus = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M7 3v8M3 7h8" />
  </svg>
);
const Check = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.5 7.5 6 11l5.5-8" />
  </svg>
);
/** HH:MM:SS:FF timecode (frames). */
function timecode(ms: number): string {
  const totalF = Math.round((ms / 1000) * FPS);
  const f = totalF % FPS;
  const s = Math.floor(totalF / FPS) % 60;
  const m = Math.floor(totalF / (FPS * 60)) % 60;
  const h = Math.floor(totalF / (FPS * 3600));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}:${p(f)}`;
}

/**
 * Bottom word-level timeline. Words sit at their real time positions and the
 * whole track is as wide as it needs to be (scrolls off-screen). The PLAYHEAD
 * stays pinned to the horizontal centre; during playback the track scrolls
 * underneath it, so the transcript flows through a fixed centre line.
 */
export function StudioTimeline() {
  const words = useEditor((s) => s.words);
  const segments = useEditor((s) => s.segments);
  const selectWord = useEditor((s) => s.selectWord);
  const selectedWordIds = useEditor((s) => s.selectedWordIds);
  const setSelectedWords = useEditor((s) => s.setSelectedWords);
  const toggleWordSelected = useEditor((s) => s.toggleWordSelected);
  const clearWordSelection = useEditor((s) => s.clearWordSelection);
  const durationMs = useEditor((s) => s.durationMs);
  const transitions = useEditor((s) => s.transitions);
  const selectedTransitionId = useEditor((s) => s.selectedTransitionId);
  const selectTransition = useEditor((s) => s.selectTransition);
  const brolls = useEditor((s) => s.brolls);
  const selectedBrollId = useEditor((s) => s.selectedBrollId);
  const selectBroll = useEditor((s) => s.selectBroll);
  const updateBroll = useEditor((s) => s.updateBroll);
  const brollLane = useEditor((s) => s.leftTab === 'broll');
  const audioLane = useEditor((s) => s.leftTab === 'audio');
  const music = useEditor((s) => s.audio.music);
  const updateMusic = useEditor((s) => s.updateMusic);
  const selectedSet = useMemo(() => new Set(selectedWordIds), [selectedWordIds]);
  // Last word of each caption line - gets a triangular right end so you can see
  // where a sentence ends on the timeline.
  const segEndIds = useMemo(
    () => new Set(segments.map((s) => s.wordIds[s.wordIds.length - 1]).filter(Boolean) as string[]),
    [segments],
  );
  const anchorRef = useRef<string | null>(null);

  const [pxPerMs, setPxPerMs] = useState(BASE_PXMS);
  const [playing, setPlaying] = useState(false);
  const [multi, setMulti] = useState(false);

  // Bottom transport = MASTER output level (final volume of everything, incl.
  // music). Distinct from the Audio tab's original-track volume.
  const volume = useEditor((s) => s.masterVolume);
  const muted = useEditor((s) => s.masterMuted);
  const setMasterVolume = useEditor((s) => s.setMasterVolume);
  const setMasterMuted = useEditor((s) => s.setMasterMuted);
  const effVol = muted ? 0 : volume;
  const [tc, setTc] = useState('00:00:00:00');
  const [activeWordId, setActiveWordId] = useState<string | null>(null);
  const [viewW, setViewW] = useState(800);

  const scrollRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const pxRef = useRef(pxPerMs);
  pxRef.current = pxPerMs;
  const insetRef = useRef(viewW / 2);
  insetRef.current = viewW / 2;

  const dur = Math.max(durationMs, words.at(-1)?.endMs ?? 0, 1000);
  const inset = viewW / 2;
  // Leading + trailing inset (half a viewport each) so t=0 and t=end can centre.
  const trackWidth = inset * 2 + dur * pxPerMs;

  // Measure the viewport width (drives centring + inset), keep it fresh on resize.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ticks = useMemo(() => {
    const stepS = pxPerMs > 0.35 ? 1 : pxPerMs > 0.14 ? 2 : pxPerMs > 0.06 ? 5 : 10;
    const out: number[] = [];
    for (let s = 0; s * 1000 <= dur; s += stepS) out.push(s);
    return out;
  }, [dur, pxPerMs]);

  useEffect(() => {
    let last = 0;
    const unsub = playbackClock.subscribe((t, isPlaying) => {
      const sc = scrollRef.current;
      // Playhead tracks current time within the track…
      if (playheadRef.current)
        playheadRef.current.style.left = `${insetRef.current + t * pxRef.current}px`;
      // …and during playback the track scrolls so it sits at the viewport centre.
      if (sc && playbackClock.isPlaying()) sc.scrollLeft = t * pxRef.current;
      // Reflect play/pause IMMEDIATELY (not throttled) so the button icon never
      // gets stuck - the clock's final pause emit must not be swallowed by the
      // throttle below. Setting the same value re-renders nothing.
      setPlaying(isPlaying);
      const now = performance.now();
      if (now - last < 90) return;
      last = now;
      setTc(timecode(t));
      let a: string | null = null;
      for (const w of words) if (t >= w.startMs && t < w.endMs) { a = w.id; break; }
      setActiveWordId(a);
    });
    return unsub;
  }, [words]);

  const seekToX = (clientX: number) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const rect = sc.getBoundingClientRect();
    const ms = (sc.scrollLeft + (clientX - rect.left) - insetRef.current) / pxRef.current;
    playbackClock.seek(Math.max(0, Math.min(dur, ms)));
  };

  /**
   * Scrub: seek on press, then follow the pointer until release. Started from
   * the playhead knob or from any empty part of the timeline. Playback pauses
   * while scrubbing and resumes after; near the viewport edges the track
   * auto-scrolls so you can drag past what's visible.
   */
  const [scrubbing, setScrubbing] = useState(false);
  const startScrub = (clientX: number) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const wasPlaying = playbackClock.isPlaying();
    if (wasPlaying) playbackClock.pause();
    let x = clientX;
    seekToX(x);
    setScrubbing(true);
    let raf = 0;
    const EDGE = 48;
    const tick = () => {
      const rect = sc.getBoundingClientRect();
      const dl = x - rect.left;
      const dr = rect.right - x;
      const step = dl < EDGE ? -Math.ceil((EDGE - dl) / 3) : dr < EDGE ? Math.ceil((EDGE - dr) / 3) : 0;
      if (step) {
        sc.scrollLeft += step;
        seekToX(x);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const move = (e: PointerEvent) => {
      x = e.clientX;
      seekToX(x);
    };
    const up = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      document.body.classList.remove('stl-scrubbing');
      setScrubbing(false);
      if (wasPlaying) playbackClock.play();
    };
    document.body.classList.add('stl-scrubbing');
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // Selection semantics: plain click = select just this word (+ seek); Shift =
  // contiguous range from the anchor (first) to this word (last); Cmd/Ctrl =
  // toggle this word in/out. The selection is for bulk effects later.
  const pickWord = (w: TranscriptWord, index: number, e: React.MouseEvent) => {
    selectWord(w.id);
    if (e.shiftKey && anchorRef.current) {
      const ai = words.findIndex((x) => x.id === anchorRef.current);
      if (ai >= 0) {
        const a = Math.min(ai, index);
        const b = Math.max(ai, index);
        setSelectedWords(words.slice(a, b + 1).map((x) => x.id));
      } else {
        setSelectedWords([w.id]);
        anchorRef.current = w.id;
      }
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      toggleWordSelected(w.id);
      anchorRef.current = w.id;
      return;
    }
    // Plain click: single-select, seek, centre, set the range anchor.
    setSelectedWords([w.id]);
    anchorRef.current = w.id;
    playbackClock.seek(w.startMs);
    const sc = scrollRef.current;
    if (sc) sc.scrollLeft = w.startMs * pxRef.current;
  };

  // Drag a B-roll block in the lane: move it, or drag an edge to extend/shrink
  // its duration. Converts pixel movement to ms via the current zoom.
  const dragBroll = (b: Broll, e: React.PointerEvent, mode: 'move' | 'left' | 'right') => {
    e.stopPropagation();
    e.preventDefault();
    selectBroll(b.id);
    const startX = e.clientX;
    const orig = { start: b.startMs, dur: b.durationMs };
    const px = pxRef.current;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const dMs = (ev.clientX - startX) / px;
      if (mode === 'move') {
        updateBroll(b.id, { startMs: Math.max(0, Math.round(orig.start + dMs)) });
      } else if (mode === 'right') {
        updateBroll(b.id, { durationMs: Math.max(200, Math.round(orig.dur + dMs)) });
      } else {
        const end = orig.start + orig.dur;
        const newStart = Math.min(end - 200, Math.max(0, Math.round(orig.start + dMs)));
        updateBroll(b.id, { startMs: newStart, durationMs: end - newStart });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Drag the music block in the audio lane: move it, or drag an edge to extend/
  // shrink the window it plays over.
  const dragMusic = (e: React.PointerEvent, mode: 'move' | 'left' | 'right') => {
    e.stopPropagation();
    e.preventDefault();
    if (!music) return;
    const startX = e.clientX;
    const orig = { start: music.startMs ?? 0, dur: music.durationMs ?? dur };
    const px = pxRef.current;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const dMs = (ev.clientX - startX) / px;
      if (mode === 'move') {
        updateMusic({ startMs: Math.max(0, Math.round(orig.start + dMs)) });
      } else if (mode === 'right') {
        updateMusic({ durationMs: Math.max(500, Math.round(orig.dur + dMs)) });
      } else {
        const end = orig.start + orig.dur;
        const newStart = Math.min(end - 500, Math.max(0, Math.round(orig.start + dMs)));
        updateMusic({ startMs: newStart, durationMs: end - newStart });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // "Fit" resets to the comfortable default zoom (100%) and recentres on the
  // current time, rather than cramming the whole clip into the viewport.
  const fit = () => {
    setPxPerMs(BASE_PXMS);
    const sc = scrollRef.current;
    if (sc) sc.scrollLeft = playbackClock.timeMs() * BASE_PXMS;
  };

  return (
    <div className="studio-bottom">
      <div className="stl-controls">
        <div className="stl-left">
          <button className="stl-play" onClick={() => playbackClock.toggle()} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause /> : <Play />}
          </button>
          <div className="stl-vol">
            <button
              className="stl-icon vol-btn"
              onClick={() => setMasterMuted(!muted)}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              <VolIcon level={effVol} />
            </button>
            <input
              className="stl-vol-slider"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={effVol}
              style={{ ['--fill' as string]: `${effVol * 100}%` }}
              onChange={(e) => setMasterVolume(Number(e.target.value))}
              aria-label="Volume"
            />
          </div>
          <button className={multi ? 'stl-chip on' : 'stl-chip'} onClick={() => setMulti((v) => !v)}>
            Select multiple
          </button>
          {selectedWordIds.length > 0 && (
            <button className="stl-chip done" onClick={clearWordSelection}>
              <Check /> Done · {selectedWordIds.length}
            </button>
          )}
        </div>
        <div className="stl-time">
          <span className="tc-cur">{tc}</span>
          <span className="tc-sep">/</span>
          <span className="tc-tot">{timecode(dur)}</span>
        </div>
        <div className="stl-right">
          <TimelineMinimap
            scrollRef={scrollRef}
            pxPerMs={pxPerMs}
            viewW={viewW}
            dur={dur}
            words={words}
            brolls={brolls}
          />
          <button className="stl-icon" onClick={() => setPxPerMs((p) => Math.max(0.02, p / 1.4))} aria-label="Zoom out">
            <Minus />
          </button>
          <span className="stl-zoom">{Math.round((pxPerMs / BASE_PXMS) * 100)}%</span>
          <button className="stl-icon" onClick={() => setPxPerMs((p) => Math.min(1.5, p * 1.4))} aria-label="Zoom in">
            <Plus />
          </button>
          <button className="stl-chip" onClick={fit}>
            Fit
          </button>
        </div>
      </div>

      <div className="stl-viewport">
        <div
          className={scrubbing ? 'stl-scroll scrubbing' : 'stl-scroll'}
          ref={scrollRef}
          onMouseDown={(e) => {
            // Words / clips stop propagation; only empty timeline + ruler scrub.
            if (e.button === 0) startScrub(e.clientX);
          }}
        >
          <div className="stl-track" style={{ width: trackWidth }}>
            <div className="stl-ruler">
              {ticks.map((s) => (
                <span key={s} className="stl-tick" style={{ left: inset + s * 1000 * pxPerMs }}>
                  {s}s
                </span>
              ))}
            </div>
            <div className="stl-words">
              {words.map((w, i) => (
                <button
                  key={w.id}
                  className={[
                    'stl-word',
                    selectedSet.has(w.id) && 'sel',
                    w.id === activeWordId && 'active',
                    w.emphasized && 'emph',
                    segEndIds.has(w.id) && 'seg-end',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    left: inset + w.startMs * pxPerMs,
                    width: Math.max(20, (w.endMs - w.startMs) * pxPerMs),
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    pickWord(w, i, e);
                  }}
                  title={w.text}
                >
                  <span className="sw-fill">{w.text}</span>
                </button>
              ))}
            </div>
            {transitions.length > 0 && (
              <div className="stl-transitions">
                {transitions.map((tr) => (
                  <button
                    key={tr.id}
                    className={tr.id === selectedTransitionId ? 'stl-tr on' : 'stl-tr'}
                    style={{ left: inset + tr.startMs * pxPerMs, width: Math.max(10, tr.durationMs * pxPerMs) }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      selectTransition(tr.id);
                      playbackClock.seek(tr.startMs);
                    }}
                    title={`${TRANSITION_LABEL[tr.kind]} · ${(tr.durationMs / 1000).toFixed(2)}s`}
                  >
                    <span className="stl-tr-label">{TRANSITION_LABEL[tr.kind]}</span>
                  </button>
                ))}
              </div>
            )}
            {audioLane && (
              <div className="stl-broll-lane">
                {!music && <span className="stl-broll-hint">Add background music to place it here</span>}
                {music && (
                  <div
                    className="stl-brl stl-aud on"
                    style={{ left: inset + (music.startMs ?? 0) * pxPerMs, width: Math.max(24, (music.durationMs ?? dur) * pxPerMs) }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => dragMusic(e, 'move')}
                    title={music.name}
                  >
                    <span className="stl-brl-h l" onPointerDown={(e) => dragMusic(e, 'left')} />
                    <span className="stl-brl-label">♪ {music.name}</span>
                    <span className="stl-brl-h r" onPointerDown={(e) => dragMusic(e, 'right')} />
                  </div>
                )}
              </div>
            )}
            {brollLane && (
              <div className="stl-broll-lane">
                {brolls.length === 0 && <span className="stl-broll-hint">Add B-roll to place it here</span>}
                {brolls.map((b) => (
                  <div
                    key={b.id}
                    className={b.id === selectedBrollId ? 'stl-brl on' : 'stl-brl'}
                    style={{ left: inset + b.startMs * pxPerMs, width: Math.max(24, b.durationMs * pxPerMs) }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => dragBroll(b, e, 'move')}
                    title={b.name}
                  >
                    <span className="stl-brl-h l" onPointerDown={(e) => dragBroll(b, e, 'left')} />
                    <span className="stl-brl-label">{b.name}</span>
                    <span className="stl-brl-h r" onPointerDown={(e) => dragBroll(b, e, 'right')} />
                  </div>
                ))}
              </div>
            )}
            <div className="stl-playhead" ref={playheadRef} style={{ left: inset }}>
              <span
                className="stl-playhead-grip"
                role="slider"
                aria-label="Playhead"
                aria-valuemin={0}
                aria-valuemax={Math.round(dur)}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startScrub(e.clientX);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Timeline navigator: the WHOLE clip in a small strip. The highlighted window
 * is the part of the timeline currently in view - drag it (or press anywhere on
 * the strip) to scroll the timeline. Shows words, B-roll, hooks and the playhead.
 * Scrolls only; it never moves the playhead.
 */
const MINI_W = 180;
function TimelineMinimap({ scrollRef, pxPerMs, viewW, dur, words, brolls }: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  pxPerMs: number;
  viewW: number;
  dur: number;
  words: TranscriptWord[];
  brolls: Broll[];
}) {
  const hooks = useEditor((s) => s.hooks);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragging, setDragging] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  // Mirror the timeline's scroll position (rAF-throttled).
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrollLeft(sc.scrollLeft);
      });
    };
    setScrollLeft(sc.scrollLeft);
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      sc.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [scrollRef]);

  // Playhead marker follows the clock without re-rendering.
  useEffect(() => {
    return playbackClock.subscribe((t) => {
      if (headRef.current) headRef.current.style.left = `${(Math.min(dur, t) / dur) * 100}%`;
    });
  }, [dur]);

  // Visible time range: the timeline centres time `scrollLeft / px`.
  const centreMs = scrollLeft / pxPerMs;
  const spanMs = viewW / pxPerMs;
  const t0 = Math.max(0, centreMs - spanMs / 2);
  const t1 = Math.min(dur, centreMs + spanMs / 2);
  const winLeft = (t0 / dur) * 100;
  const winWidth = Math.max(4, ((t1 - t0) / dur) * 100);
  const fitsAll = t0 <= 0 && t1 >= dur;

  /** Press: centre the view on the pressed point (unless grabbing the window,
   *  which keeps its grab offset), then follow the pointer. */
  const onPointerDown = (e: React.PointerEvent) => {
    const strip = stripRef.current;
    const sc = scrollRef.current;
    if (!strip || !sc || e.button !== 0) return;
    e.preventDefault();
    const rect = strip.getBoundingClientRect();
    const msAt = (x: number) => ((x - rect.left) / rect.width) * dur;
    const pressMs = msAt(e.clientX);
    const inWindow = pressMs >= t0 && pressMs <= t1;
    const grab = inWindow ? pressMs - centreMs : 0;
    const go = (x: number) => {
      const c = Math.max(0, Math.min(dur, msAt(x) - grab));
      sc.scrollLeft = c * pxPerMs;
    };
    go(e.clientX);
    setDragging(true);
    const move = (ev: PointerEvent) => go(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const pctOf = (ms: number) => `${(ms / dur) * 100}%`;
  return (
    <div
      ref={stripRef}
      className={dragging ? 'stl-mini dragging' : 'stl-mini'}
      style={{ width: MINI_W }}
      onPointerDown={onPointerDown}
      role="scrollbar"
      aria-label="Timeline overview"
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={Math.round(dur)}
      aria-valuenow={Math.round(centreMs)}
      title="Drag to scroll the timeline"
    >
      {words.map((w) => (
        <span key={w.id} className="stl-mini-w" style={{ left: pctOf(w.startMs), width: pctOf(Math.max(40, w.endMs - w.startMs)) }} />
      ))}
      {brolls.map((b) => (
        <span key={b.id} className="stl-mini-b" style={{ left: pctOf(b.startMs), width: pctOf(b.durationMs) }} />
      ))}
      {hooks.map((h) => (
        <span key={h.id} className="stl-mini-h" style={{ left: pctOf(h.startMs), width: pctOf(h.endMs - h.startMs) }} />
      ))}
      {!fitsAll && <span className="stl-mini-win" style={{ left: `${winLeft}%`, width: `${winWidth}%` }} />}
      <div className="stl-mini-head" ref={headRef} />
    </div>
  );
}
